import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { notificationsService } from "../services/notifications.service";
import config from "../config";
import { getTokenExpirationMs } from "../utils/jwt";

const WEBSOCKET_TOKEN_MIN_REMAINING_MS = 5000;

const NotificationsContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);

  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }

  return context;
};

export const useIncomingNotifications = () => {
  const context = useContext(NotificationsContext);
  return context?.subscribeToIncoming ?? null;
};

export function NotificationsProvider({ children }) {
  const {
    auth,
    loading: authLoading,
    refreshAccessToken,
  } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const incomingListenersRef = useRef(new Set());
  const emittedIncomingIdsRef = useRef(new Set());

  const hasSession = !!auth?.refresh;

  const subscribeToIncoming = useCallback((listener) => {
    incomingListenersRef.current.add(listener);
    return () => {
      incomingListenersRef.current.delete(listener);
    };
  }, []);

  const emitIncoming = useCallback((notification) => {
    if (notification?.id != null) {
      if (emittedIncomingIdsRef.current.has(notification.id)) return;
      emittedIncomingIdsRef.current.add(notification.id);
    }

    incomingListenersRef.current.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        console.error("Error in incoming notification listener:", error);
      }
    });
  }, []);

  const resetNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const reloadNotifications = useCallback(async () => {
    if (!auth?.access) {
      resetNotifications();
      return;
    }

    setLoadingNotifications(true);

    try {
      const [listData, unreadData] = await Promise.all([
        notificationsService.list(),
        notificationsService.unreadCount(),
      ]);

      setNotifications(listData?.results || []);
      setUnreadCount(unreadData?.unread_count || 0);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [auth?.access, resetNotifications]);

  const markAsRead = useCallback(async (notificationId) => {
    const response = await notificationsService.markRead(notificationId);

    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId
          ? {
            ...n,
            is_read: true,
            read_at: response?.read_at || n.read_at,
          }
          : n,
      ),
    );

    if (typeof response?.unread_count === "number") {
      setUnreadCount(response.unread_count);
    }

    return response;
  }, []);

  const markAllAsRead = useCallback(async () => {
    const response = await notificationsService.markAllRead();

    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        is_read: true,
      })),
    );

    setUnreadCount(
      typeof response?.unread_count === "number" ? response.unread_count : 0,
    );

    return response;
  }, []);

  useEffect(() => {
    if (authLoading) return;
    reloadNotifications();
  }, [authLoading, reloadNotifications]);

  useEffect(() => {
    if (authLoading) return;

    if (!hasSession) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      reconnectAttemptsRef.current = 0;
      return;
    }

    let isUnmounted = false;

    const scheduleReconnect = () => {
      if (isUnmounted) return;

      const delay = Math.min(3000 * (reconnectAttemptsRef.current + 1), 15000);

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = async () => {
      try {
        const protocol = window.location.protocol === "https:" ? "wss" : "ws";

        const storedAccessToken = localStorage.getItem("access_token");
        const storedTokenExpirationMs = getTokenExpirationMs(storedAccessToken);
        const storedTokenIsUsable =
          storedTokenExpirationMs != null &&
          storedTokenExpirationMs - Date.now() > WEBSOCKET_TOKEN_MIN_REMAINING_MS;

        let token;
        if (storedTokenIsUsable) {
          token = storedAccessToken;
        } else if (typeof refreshAccessToken === "function") {
          token = await refreshAccessToken();
        } else {
          token = storedAccessToken;
        }

        if (!token || isUnmounted) {
          if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
          }
          return;
        }

        const socket = new WebSocket(
          `${protocol}://${config.wsBaseUrl}/ws/v1/notifications/?token=${encodeURIComponent(token)}`,
        );

        socketRef.current = socket;

        socket.onopen = () => {
          reconnectAttemptsRef.current = 0;

          socket.send(
            JSON.stringify({
              event: "notification:sync",
              data: {},
            }),
          );
        };

        socket.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);

            if (payload.event === "notification:new") {
              const incoming = payload.data?.notification;
              const nextUnreadCount = payload.data?.unread_count;

              if (incoming) {
                setNotifications((prev) => {
                  const exists = prev.some((n) => n.id === incoming.id);
                  if (exists) return prev;
                  return [incoming, ...prev];
                });

                emitIncoming(incoming);
              }

              if (typeof nextUnreadCount === "number") {
                setUnreadCount(nextUnreadCount);
              }

              return;
            }

            if (payload.event === "notification:read") {
              const notificationId = payload.data?.notification_id;
              const nextUnreadCount = payload.data?.unread_count;

              if (notificationId) {
                setNotifications((prev) =>
                  prev.map((n) =>
                    n.id === notificationId
                      ? {
                        ...n,
                        is_read: true,
                        read_at: payload.data?.read_at || n.read_at,
                      }
                      : n,
                  ),
                );
              }

              if (typeof nextUnreadCount === "number") {
                setUnreadCount(nextUnreadCount);
              }

              return;
            }

            if (payload.event === "notification:readAll") {
              const nextUnreadCount = payload.data?.unread_count ?? 0;

              setNotifications((prev) =>
                prev.map((n) => ({
                  ...n,
                  is_read: true,
                })),
              );

              setUnreadCount(nextUnreadCount);
              return;
            }

            if (payload.event === "notification:sync") {
              const items = payload.data?.items || [];
              const nextUnreadCount = payload.data?.unread_count || 0;

              setNotifications(items);
              setUnreadCount(nextUnreadCount);
              return;
            }

            if (payload.event === "error") {
              console.error("Notifications WS error event:", payload.data);
            }
          } catch (error) {
            console.error("Invalid notifications WS payload:", error);
          }
        };

        socket.onerror = (error) => {
          console.error("Notifications WS error:", error);
        };

        socket.onclose = (event) => {
          console.log("WS closed", {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });

          socketRef.current = null;

          if (isUnmounted || !hasSession) return;

          // No reintentes si fue cierre limpio por desmontaje local
          if (event.code === 1000 && event.wasClean) return;

          reconnectAttemptsRef.current += 1;
          scheduleReconnect();
        };
      } catch (error) {
        console.error("Error creating notifications WS:", error);

        if (isUnmounted || !hasSession) return;

        reconnectAttemptsRef.current += 1;
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      isUnmounted = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [authLoading, hasSession, refreshAccessToken, resetNotifications, emitIncoming]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loadingNotifications,
      reloadNotifications,
      markAsRead,
      markAllAsRead,
      subscribeToIncoming,
    }),
    [
      notifications,
      unreadCount,
      loadingNotifications,
      reloadNotifications,
      markAsRead,
      markAllAsRead,
      subscribeToIncoming,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
